import { dxtToLocalServerTime, getMomentTz } from "@dexteel/mesf-core";
import type {
	ExcelCell,
	ExcelExportParams,
	ExcelRow,
	ExcelStyle,
} from "ag-grid-community";
import {
	TEST_SHACK_REPORT_STATUS,
	type TestShackReportInspection,
	type TestShackReportStatus,
} from "../models/TestShackReport";
import type {
	TestShackReportCellBase,
	TestShackReportColumnBase,
	TestShackReportSectionRevBase,
	TestShackReportSectionRevRow,
} from "../models/TestShackReportSectionRev";
import {
	formatMeasurement,
	formatOutOfSpec,
	formatRevision,
	STATUS_LABEL,
} from "./reportFormat";

const moment = getMomentTz();

const TEST_SHACK_REPORT_EXCEL_SHEET_NAME = "Inspections";

const EXCEL_STYLE = {
	SECTION_REV_HEADER: "tsr-section-rev-header",
	COLUMN_HEADER: "tsr-column-header",
	SPEC_LABEL: "tsr-spec-label",
	SPEC_CELL: "tsr-spec-cell",
	META_ID: "tsr-meta-id",
	META_CELL: "tsr-meta-cell",
	STATUS_IN: "tsr-status-in",
	STATUS_OVER: "tsr-status-over",
	STATUS_UNDER: "tsr-status-under",
	STATUS_NO_SPEC: "tsr-status-no-spec",
} as const;

const EXCEL_COLOR = {
	WHITE: "#FFFFFF",
	SLATE_900: "#263238",
	SLATE_800: "#37474F",
	SLATE_100: "#ECEFF1",
	BORDER: "#D0D7DE",
	GREEN: "#2E7D32",
	GREEN_BACKGROUND: "#E8F5E9",
	RED: "#C62828",
	RED_BACKGROUND: "#FFEBEE",
	ORANGE: "#E65100",
	ORANGE_BACKGROUND: "#FFF3E0",
	AMBER: "#8D6E00",
	AMBER_BACKGROUND: "#FFF8E1",
} as const;

const EXCEL_FILE_PREFIX = "test_shack_report";
const EXCEL_FILE_TIMESTAMP_FORMAT = "yyyyMMdd_HHmm";
const EXCEL_DATE_FORMAT = "MM/DD/YY";
const EXCEL_COLUMN_WIDTH = 86;
const EXCEL_ROW_HEIGHT = 18;

export const TEST_SHACK_REPORT_EXPORT_COLUMN_CAP = 48;

/** The only parts of the export that differ between mills. */
interface TestShackReportExcelFormat<
	TColumn extends TestShackReportColumnBase,
	TCell extends TestShackReportCellBase,
> {
	header: (column: TColumn) => string;
	spec: (value: number | null, column: TColumn) => string;
	reading: (cell: TCell | null, column: TColumn) => string;
}

type SectionRev<
	TColumn extends TestShackReportColumnBase,
	TCell extends TestShackReportCellBase,
> = TestShackReportSectionRevBase<TColumn, TCell>;

interface MetaField {
	header: string;
	value: (row: TestShackReportSectionRevRow) => string;
	styleId?: (row: TestShackReportSectionRevRow) => string;
}

const STATUS_STYLE: Record<TestShackReportStatus, string> = {
	[TEST_SHACK_REPORT_STATUS.IN]: EXCEL_STYLE.STATUS_IN,
	[TEST_SHACK_REPORT_STATUS.GO]: EXCEL_STYLE.STATUS_IN,
	[TEST_SHACK_REPORT_STATUS.NO_GO]: EXCEL_STYLE.STATUS_OVER,
	[TEST_SHACK_REPORT_STATUS.OVER]: EXCEL_STYLE.STATUS_OVER,
	[TEST_SHACK_REPORT_STATUS.UNDER]: EXCEL_STYLE.STATUS_UNDER,
	[TEST_SHACK_REPORT_STATUS.NO_SPEC]: EXCEL_STYLE.STATUS_NO_SPEC,
};

const formatProductionDate = (inspection: TestShackReportInspection) =>
	moment.utc(inspection.ProductionDate).format(EXCEL_DATE_FORMAT);

const META_FIELDS: readonly MetaField[] = [
	{
		header: "Inspection",
		value: ({ inspection }) => `#${inspection.ProfileInspectionId}`,
		styleId: () => EXCEL_STYLE.META_ID,
	},
	{
		header: "Date",
		value: ({ inspection }) => formatProductionDate(inspection),
	},
	{ header: "Shift", value: ({ inspection }) => inspection.Shift ?? "-" },
	{ header: "Crew", value: ({ inspection }) => inspection.Crew ?? "-" },
	{ header: "Heat", value: ({ inspection }) => inspection.HeatName ?? "-" },
	{
		header: "Inspector",
		value: ({ inspection }) => inspection.Inspector ?? "-",
	},
	{
		header: "lb/ft",
		value: ({ inspection }) => formatMeasurement(inspection.MeasuredLbsPerFoot),
	},
	{
		header: "Weight",
		value: ({ inspection }) =>
			inspection.WeightStatus === null
				? "-"
				: STATUS_LABEL[inspection.WeightStatus],
		styleId: ({ inspection, hasTolerances }) =>
			inspection.WeightStatus === null || !hasTolerances
				? EXCEL_STYLE.META_CELL
				: STATUS_STYLE[inspection.WeightStatus],
	},
	{
		header: "Out of spec",
		// Without a specification nothing can be judged, so no count either.
		value: (row) => (row.hasTolerances ? String(row.outOfSpecCount) : "-"),
	},
	{
		header: "Comments",
		value: ({ inspection }) => inspection.Comments ?? "-",
	},
];

const BORDER = {
	lineStyle: "Continuous",
	weight: 0,
	color: EXCEL_COLOR.BORDER,
} as const;

const ALL_BORDERS = {
	borderTop: BORDER,
	borderBottom: BORDER,
	borderLeft: BORDER,
	borderRight: BORDER,
};

export const TEST_SHACK_REPORT_EXCEL_STYLES: ExcelStyle[] = [
	{
		id: EXCEL_STYLE.SECTION_REV_HEADER,
		font: { bold: true, color: EXCEL_COLOR.WHITE, size: 11 },
		interior: { color: EXCEL_COLOR.SLATE_800, pattern: "Solid" },
		alignment: { horizontal: "Left", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: EXCEL_STYLE.COLUMN_HEADER,
		font: { bold: true, color: EXCEL_COLOR.SLATE_800, size: 10 },
		interior: { color: EXCEL_COLOR.SLATE_100, pattern: "Solid" },
		alignment: { horizontal: "Center", vertical: "Center", wrapText: true },
		borders: ALL_BORDERS,
	},
	{
		id: EXCEL_STYLE.SPEC_LABEL,
		font: { bold: true, color: EXCEL_COLOR.SLATE_800, size: 10 },
		alignment: { horizontal: "Left", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: EXCEL_STYLE.SPEC_CELL,
		font: { color: EXCEL_COLOR.SLATE_800, size: 10 },
		alignment: { horizontal: "Center", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: EXCEL_STYLE.META_ID,
		font: { bold: true, color: EXCEL_COLOR.SLATE_900, size: 10 },
		alignment: { horizontal: "Left", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: EXCEL_STYLE.META_CELL,
		font: { color: EXCEL_COLOR.SLATE_900, size: 10 },
		alignment: { horizontal: "Left", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: EXCEL_STYLE.STATUS_IN,
		font: { color: EXCEL_COLOR.GREEN, size: 10 },
		interior: { color: EXCEL_COLOR.GREEN_BACKGROUND, pattern: "Solid" },
		alignment: { horizontal: "Center", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: EXCEL_STYLE.STATUS_OVER,
		font: { bold: true, color: EXCEL_COLOR.RED, size: 10 },
		interior: { color: EXCEL_COLOR.RED_BACKGROUND, pattern: "Solid" },
		alignment: { horizontal: "Center", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: EXCEL_STYLE.STATUS_UNDER,
		font: { bold: true, color: EXCEL_COLOR.ORANGE, size: 10 },
		interior: { color: EXCEL_COLOR.ORANGE_BACKGROUND, pattern: "Solid" },
		alignment: { horizontal: "Center", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: EXCEL_STYLE.STATUS_NO_SPEC,
		font: { color: EXCEL_COLOR.AMBER, size: 10 },
		interior: { color: EXCEL_COLOR.AMBER_BACKGROUND, pattern: "Solid" },
		alignment: { horizontal: "Center", vertical: "Center" },
		borders: ALL_BORDERS,
	},
];

const cell = (value: string, styleId: string, span = 1): ExcelCell => ({
	styleId,
	...(span > 1 ? { mergeAcross: span - 1 } : {}),
	data: { type: "String", value },
});

const formatPeriod = (start: Date, end: Date) => {
	const from = moment.utc(start).format(EXCEL_DATE_FORMAT);
	const to = moment.utc(end).format(EXCEL_DATE_FORMAT);
	return from === to ? from : `${from} - ${to}`;
};

const sectionRevHeaderRow = (
	sectionRev: TestShackReportSectionRevBase,
	totalColumns: number,
): ExcelRow => {
	const inspectionCount = sectionRev.rows.length;
	const title = [
		sectionRev.sectionName,
		formatRevision(sectionRev.specRev, sectionRev.specSubRev),
		inspectionCount === 1 ? "1 inspection" : `${inspectionCount} inspections`,
		formatPeriod(sectionRev.firstProductionDate, sectionRev.lastProductionDate),
		formatOutOfSpec(sectionRev),
	].join(" · ");

	return {
		cells: [cell(title, EXCEL_STYLE.SECTION_REV_HEADER, totalColumns)],
	};
};

const sectionRevRows = <
	TColumn extends TestShackReportColumnBase,
	TCell extends TestShackReportCellBase,
>(
	sectionRev: SectionRev<TColumn, TCell>,
	totalColumns: number,
	showMinTargetMax: boolean,
	format: TestShackReportExcelFormat<TColumn, TCell>,
): ExcelRow[] => {
	const { columns } = sectionRev;
	const specRow = (
		label: string,
		value: (column: TColumn) => number | null,
	): ExcelRow => ({
		cells: [
			cell(label, EXCEL_STYLE.SPEC_LABEL),
			...META_FIELDS.slice(1).map(() => cell("", EXCEL_STYLE.SPEC_LABEL)),
			...columns.map((column) =>
				cell(format.spec(value(column), column), EXCEL_STYLE.SPEC_CELL),
			),
		],
	});
	const inspectionRow = (
		row: TestShackReportSectionRevRow<TCell>,
	): ExcelRow => ({
		cells: [
			...META_FIELDS.map((field) =>
				cell(field.value(row), field.styleId?.(row) ?? EXCEL_STYLE.META_CELL),
			),
			...row.cells.map((readingCell, index) =>
				cell(
					format.reading(readingCell, columns[index]),
					// Without a specification readings stay neutral, except NG/No checks.
					readingCell === null ||
						(!sectionRev.hasTolerances &&
							readingCell.status !== TEST_SHACK_REPORT_STATUS.NO_GO)
						? EXCEL_STYLE.SPEC_CELL
						: STATUS_STYLE[readingCell.status],
				),
			),
		],
	});

	return [
		sectionRevHeaderRow(sectionRev, totalColumns),
		{
			cells: [
				...META_FIELDS.map((field) =>
					cell(field.header, EXCEL_STYLE.COLUMN_HEADER),
				),
				...columns.map((column) =>
					cell(format.header(column), EXCEL_STYLE.COLUMN_HEADER),
				),
			],
		},
		...(showMinTargetMax
			? [
					specRow("Min", (column) => column.ltl),
					specRow("Target", (column) => column.nominal),
					specRow("Max", (column) => column.utl),
				]
			: []),
		...sectionRev.rows.map(inspectionRow),
		{ cells: [] },
	];
};

export const testShackReportExcelParams = <
	TColumn extends TestShackReportColumnBase,
	TCell extends TestShackReportCellBase,
>(
	sectionRevs: SectionRev<TColumn, TCell>[],
	showMinTargetMax: boolean,
	format: TestShackReportExcelFormat<TColumn, TCell>,
): ExcelExportParams => {
	const totalColumns = sectionRevs.reduce(
		(widest, sectionRev) =>
			Math.max(widest, META_FIELDS.length + sectionRev.columns.length),
		META_FIELDS.length,
	);
	const rows = sectionRevs.flatMap((sectionRev) =>
		sectionRevRows(sectionRev, totalColumns, showMinTargetMax, format),
	);

	return {
		fileName: `${EXCEL_FILE_PREFIX}_${dxtToLocalServerTime(new Date(), EXCEL_FILE_TIMESTAMP_FORMAT)}.xlsx`,
		sheetName: TEST_SHACK_REPORT_EXCEL_SHEET_NAME,
		columnKeys: Array.from(
			{ length: TEST_SHACK_REPORT_EXPORT_COLUMN_CAP },
			(_value, index) => `col${index}`,
		),
		shouldRowBeSkipped: () => true,
		processHeaderCallback: () => "",
		columnWidth: () => EXCEL_COLUMN_WIDTH,
		rowHeight: EXCEL_ROW_HEIGHT,
		prependContent: rows,
	};
};
